import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { appendServiceRequest } from "../../utils/adminInboxStore";
import { mirrorPublicServiceRequestToLocal } from "../../config/publicApiFeature";
import { submitServiceRequestPublic } from "../../controllers/publicServiceRequestController";
import { createServiceRequestModalResolver } from "./serviceRequestModalResolver";

const DEFAULT_FORM = {
  requesterType: "company",
  name: "",
  companyName: "",
  email: "",
  requirements: "",
  description: "",
};

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ModalSparkIcon({ gradId }) {
  const gid = `vh-rq-grad-${gradId}`;
  return (
    <svg
      className="vh-request-modal__spark"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="20" cy="20" r="19" stroke={`url(#${gid})`} strokeWidth="1.2" />
      <path
        d="M20 11v6l4 2"
        stroke={`url(#${gid})`}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id={gid} x1="8" y1="8" x2="34" y2="32">
          <stop stopColor="#2c666e" />
          <stop offset="1" stopColor="#07393c" />
        </linearGradient>
      </defs>
    </svg>
  );
}

const ServiceRequestModal = ({ open, onClose, serviceTitle }) => {
  const { t, i18n } = useTranslation();
  const titleId = useId().replace(/:/g, "");
  const descId = useId().replace(/:/g, "");
  const profileSectionId = useId().replace(/:/g, "");
  const briefSectionId = useId().replace(/:/g, "");
  const sparkGradId = useId().replace(/:/g, "");
  const dialogRef = useRef(null);
  const errorsSummaryRef = useRef(null);
  const [animVisible, setAnimVisible] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");

  const resolver = useMemo(() => createServiceRequestModalResolver(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: DEFAULT_FORM,
    resolver,
    mode: "onSubmit",
  });

  const requesterType = watch("requesterType");
  const nameVal = watch("name");
  const emailVal = watch("email");
  const companyVal = watch("companyName");
  const reqVal = watch("requirements");
  const descVal = watch("description");

  useEffect(() => {
    clearErrors("root");
  }, [nameVal, emailVal, companyVal, reqVal, descVal, requesterType, clearErrors]);

  useEffect(() => {
    if (requesterType === "individual") {
      setValue("companyName", "", {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [requesterType, setValue]);

  useEffect(() => {
    if (!open) return undefined;
    const y = window.scrollY;
    const { style: bodyStyle } = document.body;
    const { style: htmlStyle } = document.documentElement;
    const prev = {
      bodyPosition: bodyStyle.position,
      bodyTop: bodyStyle.top,
      bodyLeft: bodyStyle.left,
      bodyRight: bodyStyle.right,
      bodyOverflow: bodyStyle.overflow,
      bodyWidth: bodyStyle.width,
      htmlOverflow: htmlStyle.overflow,
    };
    htmlStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${y}px`;
    bodyStyle.left = "0";
    bodyStyle.right = "0";
    bodyStyle.overflow = "hidden";
    bodyStyle.width = "100%";

    return () => {
      htmlStyle.overflow = prev.htmlOverflow;
      bodyStyle.position = prev.bodyPosition;
      bodyStyle.top = prev.bodyTop;
      bodyStyle.left = prev.bodyLeft;
      bodyStyle.right = prev.bodyRight;
      bodyStyle.overflow = prev.bodyOverflow;
      bodyStyle.width = prev.bodyWidth;
      window.scrollTo(0, y);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setAnimVisible(false);
      return;
    }
    reset(DEFAULT_FORM);
    setSubmitSuccess(false);
    setSuccessEmail("");
    setAnimVisible(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimVisible(true));
    });
    return () => cancelAnimationFrame(id);
  }, [open, reset]);

  useEffect(() => {
    if (!open) return undefined;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const getFocusable = () =>
      Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const list = getFocusable();
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener("keydown", onKeyDown);
    const focusId = window.setTimeout(() => {
      if (submitSuccess) {
        dialog.querySelector(".vh-request-modal__success-done")?.focus();
      } else {
        dialog.querySelector('input[name="name"]')?.focus();
      }
    }, 80);

    return () => {
      dialog.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(focusId);
    };
  }, [open, submitSuccess]);

  const onValidSubmit = useCallback(
    async (data) => {
      clearErrors("root");
      const localPayload = {
        requesterType: data.requesterType,
        name: data.name,
        companyName: data.companyName,
        email: data.email,
        requirements: data.requirements,
        description: data.description,
        serviceTitle: serviceTitle || "",
      };
      const apiBody = {
        email: data.email,
        name: data.name,
        company: data.requesterType === "company" ? data.companyName : "",
        requesterType: data.requesterType,
        serviceInterest: serviceTitle || "",
        requirements: data.requirements,
        description: data.description,
        message: `${data.requirements}\n\n---\n\n${data.description}`,
        metadata: { locale: i18n.language || "en" },
      };

      const r = await submitServiceRequestPublic(apiBody);
      if (!r.ok) {
        setError("root", {
          type: "server",
          message:
            r.errorMessage || t("servicePage.requestModal.submitServerError"),
        });
        return;
      }
      if (mirrorPublicServiceRequestToLocal()) {
        appendServiceRequest({
          ...localPayload,
          ...(typeof r.data?.id === "string" ? { id: r.data.id } : {}),
        });
      }

      setSuccessEmail(data.email);
      setSubmitSuccess(true);
    },
    [clearErrors, serviceTitle, i18n.language, setError, t]
  );

  const topErrors = useMemo(() => {
    const collected = [];
    if (errors.root?.message) collected.push(errors.root.message);
    ["name", "email", "companyName", "requirements", "description"].forEach(
      (key) => {
        const m = errors[key]?.message;
        if (m && typeof m === "string") collected.push(m);
      }
    );
    return [...new Set(collected)];
  }, [errors]);
  const topErrorsKey = useMemo(() => topErrors.join("\u0001"), [topErrors]);

  useEffect(() => {
    if (!open || topErrors.length === 0) return undefined;
    const run = () => {
      const el = errorsSummaryRef.current;
      if (!el) return;
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      el.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
      try {
        el.focus({ preventScroll: true });
      } catch {
        /* focus not supported on some hosts */
      }
    };
    const id = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => cancelAnimationFrame(id);
  }, [open, topErrorsKey, topErrors.length]);

  if (!open) return null;

  const node = (
    <div
      className={`vh-request-modal__backdrop${animVisible ? " vh-request-modal__backdrop--visible" : ""}`}
      role="presentation"
      onClick={onClose}
      dir={i18n.dir()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="vh-request-modal__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {submitSuccess ? (
          <div className="vh-request-modal__success-panel">
            <button
              type="button"
              className="vh-request-modal__close"
              aria-label={t("servicePage.requestModal.closeAria")}
              onClick={onClose}
            >
              <CloseIcon />
            </button>
            <div
              className="vh-request-modal__success"
              role="status"
              aria-live="polite"
            >
              <div className="vh-request-modal__success-mark" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle
                    cx="24"
                    cy="24"
                    r="22"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    opacity="0.2"
                  />
                  <path
                    d="M15 24.5l6.2 6L33 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="vh-request-modal__eyebrow">
                {t("servicePage.requestModal.successEyebrow")}
              </p>
              <h2 id={titleId} className="vh-request-modal__title">
                {t("servicePage.requestModal.successTitle")}
              </h2>
              <p id={descId} className="vh-request-modal__subtitle">
                {t("servicePage.requestModal.successBody", {
                  title: serviceTitle,
                  email: successEmail,
                })}
              </p>
              <div className="vh-request-modal__success-actions">
                <button
                  type="button"
                  className="btn-n btn-n--black vh-request-modal__btn vh-request-modal__btn--primary vh-request-modal__success-done"
                  onClick={onClose}
                >
                  <span className="btn-n__text">
                    {t("servicePage.requestModal.successDone")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <header className="vh-request-modal__header">
              <div className="vh-request-modal__hero">
                <div className="vh-request-modal__hero-visual" aria-hidden="true">
                  <ModalSparkIcon gradId={sparkGradId} />
                </div>
                <div className="vh-request-modal__hero-copy">
                  <p className="vh-request-modal__eyebrow">
                    {t("servicePage.requestModal.title")}
                  </p>
                  <h2 id={titleId} className="vh-request-modal__title">
                    {serviceTitle}
                  </h2>
                  <p id={descId} className="vh-request-modal__subtitle">
                    {t("servicePage.requestModal.subtitle", { title: serviceTitle })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="vh-request-modal__close"
                aria-label={t("servicePage.requestModal.closeAria")}
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            </header>

            <form
              className="vh-request-modal__form"
              onSubmit={handleSubmit(onValidSubmit)}
              noValidate
              aria-busy={isSubmitting}
            >
              {topErrors.length > 0 ? (
                <div
                  ref={errorsSummaryRef}
                  tabIndex={-1}
                  className="vh-request-modal__errors-top"
                  role="alert"
                  aria-live="polite"
                >
                  <ul className="vh-request-modal__errors-top-list">
                    {topErrors.map((msg, i) => (
                      <li key={`${i}-${msg}`} className="vh-request-modal__errors-top-item">
                        {msg}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="vh-request-modal__section">
                <div className="vh-request-modal__section-head">
                  <span className="vh-request-modal__section-accent" aria-hidden="true" />
                  <h3
                    id={`${profileSectionId}-label`}
                    className="vh-request-modal__section-title"
                  >
                    {t("servicePage.requestModal.sectionProfile")}
                  </h3>
                </div>

                <fieldset className="vh-request-modal__fieldset">
                  <legend className="vh-request-modal__legend">
                    {t("servicePage.requestModal.requesterLegend")}
                  </legend>
                  <div
                    className="vh-request-modal__segmented"
                    role="radiogroup"
                    aria-label={t("servicePage.requestModal.requesterLegend")}
                  >
                    <label
                      className={`vh-request-modal__segment${requesterType === "company" ? " vh-request-modal__segment--active" : ""}`}
                    >
                      <input type="radio" value="company" {...register("requesterType")} />
                      <span className="vh-request-modal__segment-text">
                        {t("servicePage.requestModal.typeCompany")}
                      </span>
                    </label>
                    <label
                      className={`vh-request-modal__segment${requesterType === "individual" ? " vh-request-modal__segment--active" : ""}`}
                    >
                      <input
                        type="radio"
                        value="individual"
                        {...register("requesterType")}
                      />
                      <span className="vh-request-modal__segment-text">
                        {t("servicePage.requestModal.typeIndividual")}
                      </span>
                    </label>
                  </div>
                </fieldset>

                <label className="vh-request-modal__field">
                  <span className="vh-request-modal__label">
                    {t("servicePage.requestModal.nameLabel")}
                    <span className="vh-request-modal__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <input
                    type="text"
                    autoComplete="name"
                    className="vh-request-modal__input"
                    placeholder={t("servicePage.requestModal.namePlaceholder")}
                    aria-invalid={Boolean(errors.name)}
                    {...register("name")}
                  />
                </label>

                {requesterType === "company" ? (
                  <label className="vh-request-modal__field vh-request-modal__field--reveal">
                    <span className="vh-request-modal__label">
                      {t("servicePage.requestModal.companyLabel")}
                      <span className="vh-request-modal__req" aria-hidden="true">
                        *
                      </span>
                    </span>
                    <input
                      type="text"
                      autoComplete="organization"
                      className="vh-request-modal__input"
                      placeholder={t("servicePage.requestModal.companyPlaceholder")}
                      aria-invalid={Boolean(errors.companyName)}
                      {...register("companyName")}
                    />
                  </label>
                ) : null}

                <label className="vh-request-modal__field">
                  <span className="vh-request-modal__label">
                    {t("servicePage.requestModal.emailLabel")}
                    <span className="vh-request-modal__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <input
                    type="email"
                    autoComplete="email"
                    className="vh-request-modal__input"
                    placeholder={t("servicePage.requestModal.emailPlaceholder")}
                    aria-invalid={Boolean(errors.email)}
                    {...register("email")}
                  />
                </label>
              </div>

              <div className="vh-request-modal__section">
                <div className="vh-request-modal__section-head">
                  <span className="vh-request-modal__section-accent" aria-hidden="true" />
                  <h3
                    id={`${briefSectionId}-label`}
                    className="vh-request-modal__section-title"
                  >
                    {t("servicePage.requestModal.sectionBrief")}
                  </h3>
                </div>

                <label className="vh-request-modal__field">
                  <span className="vh-request-modal__label">
                    {t("servicePage.requestModal.requirementsLabel")}
                    <span className="vh-request-modal__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="vh-request-modal__hint">
                    {t("servicePage.requestModal.requirementsHint")}
                  </span>
                  <textarea
                    className="vh-request-modal__textarea"
                    rows={4}
                    placeholder={t("servicePage.requestModal.requirementsPlaceholder")}
                    aria-invalid={Boolean(errors.requirements)}
                    {...register("requirements")}
                  />
                </label>

                <label className="vh-request-modal__field">
                  <span className="vh-request-modal__label">
                    {t("servicePage.requestModal.descriptionLabel")}
                    <span className="vh-request-modal__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="vh-request-modal__hint">
                    {t("servicePage.requestModal.descriptionHint")}
                  </span>
                  <textarea
                    className="vh-request-modal__textarea"
                    rows={5}
                    placeholder={t("servicePage.requestModal.descriptionPlaceholder")}
                    aria-invalid={Boolean(errors.description)}
                    {...register("description")}
                  />
                </label>
              </div>

              <div className="vh-request-modal__footer">
                <p className="vh-request-modal__footer-note">
                  {t("servicePage.requestModal.footerNote")}
                </p>
                <div className="vh-request-modal__actions">
                  <button
                    type="button"
                    className="btn-n btn-n--gray vh-request-modal__btn"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    <span className="btn-n__text">
                      {t("servicePage.requestModal.cancel")}
                    </span>
                  </button>
                  <button
                    type="submit"
                    className="btn-n btn-n--black vh-request-modal__btn vh-request-modal__btn--primary"
                    disabled={isSubmitting}
                  >
                    <span className="vh-request-modal__submit-inner">
                      {isSubmitting ? (
                        <span
                          className="vh-request-modal__spinner"
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="btn-n__text">
                        {isSubmitting
                          ? t("servicePage.requestModal.submitSending")
                          : t("servicePage.requestModal.submit")}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default ServiceRequestModal;
